import { useCallback, useMemo, useState } from 'react';
import { ChromelessPlayer as Player, PlayerConfiguration, version } from 'theoplayer';
import { DefaultUI, ErrorDisplay } from '@theoplayer/react-ui';
import { InterceptableRequest } from 'theoplayer';

import './App.css';
import { needsNetworkInterceptor, convertToSourceDescription, QueryParams } from './helpers/queryParamsHelper';

const doubled =
  '\
++++             ++++\n\
+++++++       +++++++\n\
+++++++++   +++++++++\n\
+++++++++   +++++++++\n\
++++++++++ ++++++++++\n\
+++++++++   +++++++++\n\
+++++++++   +++++++++\n\
+++++++       +++++++\n\
++++             ++++\n\
      OptiView';

console.log(doubled);
console.log(`[OptiView Player SDK Version] ${version}`);
console.log(`[OptiView Hosted Player Version] ${process.env.REACT_APP_GIT_TAG}`);

// Set up to retrieve query params
const queryString = window.location.search; // Gets the query string from the current URL
const urlParams = new URLSearchParams(queryString); // Creates a URLSearchParams object

// Required query parameters

const params: QueryParams = {
  license: urlParams.get('license') ?? undefined,
  sourceType: urlParams.get('sourceType') ?? undefined,
  source: urlParams.get('source') ?? undefined,
  token: urlParams.get('token') ?? undefined,
  autoplay: urlParams.get('autoPlay') === 'true',
  muted: urlParams.get('muted') === 'true',
  poster: urlParams.get('poster') ?? undefined,
  color: `#${urlParams.get('playerColor') ?? '4D5963'}`,
  maxDelay: Number(urlParams.get('maxDelay') ?? 120),
  minDelay: Number(urlParams.get('minDelay') ?? 50),
  maxHeight: urlParams.get('maxHeight') ?? undefined,
  maxWidth: urlParams.get('maxWidth') ?? undefined,
};

const config: PlayerConfiguration = {
    license: params.license,
    // Note: THEOplayer loads workers for transmuxing and other tasks
    // these workers need to be located on the same server (see npm run copy:theoplayer)
    libraryLocation: `${process.env.PUBLIC_URL}/vendor/theoplayer`,
    mutedAutoplay: 'all',
    allowNativeFullscreen: true,
    /* turning this top level check for the embedding page off for now
    isEmbeddable: true,
    */
};

declare module 'csstype' {
    interface Properties {
        // Custom CSS properties for hosted player
        // https://github.com/frenic/csstype#what-should-i-do-when-i-get-type-errors
        '--player-color'?: string;
    }
}

function App() {
  const source = useMemo(() => convertToSourceDescription(params), [params]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onPlayer = useCallback((player: Player): void => {
    console.log('[OptiView sourceType]', params.sourceType);

    // Set a poster
    if (params.poster !== undefined) {
      player.poster = params.poster;
    }

    // For Low Delay (THEOlive) and Live (HLS) streams that require token based security
    if (needsNetworkInterceptor(params)) {
      const interceptor = (request: InterceptableRequest) => {
        // Only add Authorization header for THEOlive request
        if (new URL(request.url).hostname.endsWith("theo.live")) {
          const headers = {
            ...request.headers,
            Authorization: `Bearer ${params.token}`,
          }
          request.redirect({
            headers,
          });
        }
      }
      player.network.addRequestInterceptor(interceptor);
    }

    player.addEventListener('error', function (error) {
      if (error) {
        window.parent.postMessage(`An error occurred with your stream.`, '*');
      }

      // Handle subscriber token error message
      if (error.errorObject.message === 'Secure stream requires Subscriber Token' && player.element.parentNode) {
        setErrorMessage('Stream requires authentication');
      }
      // All error codes can be found here: node_modules/theoplayer/THEOplayer.common.d.ts
      // if (error.errorObject.code === 4000) {
      // Display custom error message overlay
      // var customMessage = document.createElement('div');
      // customMessage.innerText = "The stream is offline. Please check back later.";
      // customMessage.style.color = 'white';
      // customMessage.style.position = 'absolute';
      // customMessage.style.top = '50%';
      // customMessage.style.left = '50%';
      // customMessage.style.transform = 'translate(-50%, -50%)';
      // player.element.appendChild(customMessage);

      // or overwrite existing error text content
      else {
        setErrorMessage('Your stream is currently unavailable. Please try again.');
      }
      // }
    });

    // POST player events from iframe to parent
    player.addEventListener(['ended', 'pause', 'play', 'resize', 'volumechange'], function (event) {
      window.parent.postMessage(event, '*');
    });

    player.addEventListener('playing', function (_) {
      // Millicast diagnostics - log connection information once connected to stream
      if (params.sourceType !== undefined && params.sourceType.toLowerCase() === 'realtime') {
        const diagnostics = player.millicast.diagnose({
          historySize: 15,
          minLogLevel: 'DEBUG',
          statsCount: 1,
          statsFormat: 'JSON',
        });

        console.log('[OptiView Player realtime connection diagnostics]', diagnostics);
      }
    });
  }, []);

  return (
    <div className="App">
      <DefaultUI
        onReady={onPlayer}
        configuration={config}
        source={source}
        muted={params.muted}
        autoplay={params.autoplay}
        style={{ '--player-color': params.color }}
        error={<ErrorDisplay>{errorMessage && <span slot="message">{errorMessage}</span>}</ErrorDisplay>}
      />
    </div>
  );
}

export default App;
